<script setup lang="ts">
import { computed, useAttrs, useTemplateRef } from "vue";
import type { TippyOptions } from "vue-tippy";

import BaseTooltip from "@/components/ui/BaseTooltip/BaseTooltip.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import type { ClickableProps } from "@/composables/Props";

const props = defineProps<
  ClickableProps & {
    size?: "x-small" | "small" | "medium";
    scale?: "fixed" | "board";
    keyOverride?: string;
    position?: TippyOptions["placement"];
    offset?: TippyOptions["offset"];
    activated?: boolean;
    disabled?: boolean;
  }
>();

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();
const buttonRef = useTemplateRef<HTMLButtonElement>("button");

const ariaPressed = computed(() =>
  attrs.role || attrs["aria-controls"] ? undefined : props.activated,
);
const ariaExpanded = computed(() => (attrs["aria-controls"] ? props.activated : undefined));

// Allow parent to focus the button and call getBoundingClientRect
defineExpose({
  blur: () => void buttonRef.value?.blur(),
  focus: () => void buttonRef.value?.focus(),
  getBoundingClientRect: () => buttonRef.value!.getBoundingClientRect(),
});
</script>

<template>
  <BaseTooltip :key="keyOverride ?? text" :position :offset>
    <template v-if="$slots.tooltip" #content>
      <slot name="tooltip" />
    </template>
    <template v-else-if="text" #content>
      {{ text }}
      <span class="shortcut">{{ shortcut }}</span>
    </template>
    <button
      ref="button"
      class="icon-button"
      :class="[size, scale && `scale-${scale}`, { activated }]"
      v-bind="$attrs"
      :aria-label="ariaLabel || text"
      :aria-pressed
      :aria-expanded
      :disabled
    >
      <SvgIcon :name="icon || 'system/placeholder'" class="icon" />
    </button>
  </BaseTooltip>
</template>

<style lang="scss" scoped>
@use "@/styles/variables/colors";

.icon-button {
  display: inline-flex;
  outline: revert;
  outline-offset: revert;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 6px;
  color: colors.$icon-subtle;
  cursor: pointer;
  flex-shrink: 0;

  &.x-small {
    width: 20px;
    height: 20px;
    padding: 2px;

    :deep(svg) {
      width: 16px;
      height: 16px;
    }
  }

  &.small {
    width: 32px;
    height: 32px;
    padding: 6px;

    .icon {
      width: 20px;
      height: 20px;
    }
  }

  &.medium {
    width: 36px;
    height: 36px;
    padding: 8px;

    .icon {
      width: 20px;
      height: 20px;
    }
  }

  &:not(.x-small, .small, .medium) {
    width: 36px;
    height: 36px;
    padding: 8px;

    .icon {
      width: 20px;
      height: 20px;
    }
  }

  // Board scale - uses em units to scale with board's font-size context
  &.scale-board {
    width: 1.25em;
    height: 1.25em;
    padding: 0.125em;
    border-radius: 0.125em;

    .icon {
      width: 0.75em;
      height: 0.75em;
    }
  }

  // Disabled state
  &:disabled {
    background-color: colors.$background-disabled;
    color: colors.$icon-disabled;
    cursor: not-allowed;

    .icon {
      color: colors.$icon-disabled;
    }
  }

  // Activated state (toggle on)
  &.activated:not(:disabled) {
    background-color: colors.$main;
    color: colors.$icon-inverse;

    .icon {
      color: colors.$icon-inverse;
    }
  }

  // Hover state
  &:not(:disabled, .activated):hover {
    background-color: colors.$background-alpha-hover;
    color: colors.$icon;
  }

  // Active state (pressed)
  &:not(:disabled, .activated):active {
    background-color: colors.$background-alpha-active;
    color: colors.$icon;
  }
}
</style>
